/**
 * Serviço para integração com a webhook da Amazon configurada no dashboard
 */

import { doc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Interfaces para a resposta da webhook da Amazon
interface AmazonOffer {
  Shipping: {
    CurrencyCode: string;
    Amount: number;
  };
  ListingPrice: {
    CurrencyCode: string;
    Amount: number;
  };
  ShippingTime: {
    maximumHours: number;
    minimumHours: number;
    availabilityType: string;
  };
  SellerFeedbackRating: {
    FeedbackCount: number;
    SellerPositiveFeedbackRating: number;
  };
  ShipsFrom?: {
    Country: string;
  };
  PrimeInformation: {
    IsPrime?: boolean;
    IsNationalPrime?: boolean;
  };
  SubCondition: string;
  SellerId: string;
  IsFeaturedMerchant: boolean;
  IsBuyBoxWinner: boolean;
  IsFulfilledByAmazon: boolean;
}

interface AmazonSummary {
  LowestPrices: Array<{
    condition: string;
    fulfillmentChannel: string;
    LandedPrice: {
      CurrencyCode: string;
      Amount: number;
    };
    ListingPrice: {
      CurrencyCode: string;
      Amount: number;
    };
    Shipping: {
      CurrencyCode: string;
      Amount: number;
    };
  }>;
  BuyBoxPrices: Array<{
    condition: string;
    LandedPrice: {
      CurrencyCode: string;
      Amount: number;
    };
    ListingPrice: {
      CurrencyCode: string;
      Amount: number;
    };
    Shipping: {
      CurrencyCode: string;
      Amount: number;
    };
  }>;
  NumberOfOffers: Array<{
    condition: string;
    fulfillmentChannel: string;
    OfferCount: number;
  }>;
  BuyBoxEligibleOffers: Array<{
    condition: string;
    fulfillmentChannel: string;
    OfferCount: number;
  }>;
  SalesRankings: Array<{
    ProductCategoryId: string;
    Rank: number;
  }>;
  CompetitivePriceThreshold?: {
    CurrencyCode: string;
    Amount: number;
  };
  TotalOfferCount: number;
}

interface AmazonProductSummary {
  marketplaceId: string;
  adultProduct: boolean;
  autographed: boolean;
  brand: string;
  browseClassification: {
    displayName: string;
    classificationId: string;
  };
  color: string;
  itemClassification: string;
  itemName: string;
  manufacturer: string;
  memorabilia: boolean;
  partNumber: string;
  releaseDate: string;
  size: string;
  tradeInEligible: boolean;
  websiteDisplayGroup: string;
  websiteDisplayGroupName: string;
}

interface AmazonProductData {
  ASIN: string;
  Title: string;
  Offers: AmazonOffer[];
  status: string;
  Summary: AmazonSummary;
  summaries: AmazonProductSummary[];
  Identifier: {
    MarketplaceId: string;
    ItemCondition: string;
    ASIN: string;
  };
  salesRanks: any[];
  ItemCondition: string;
  marketplaceId: string;
  CompetitivePricing: {
    CompetitivePrices: Array<{
      belongsToRequester: boolean;
      condition: string;
      subcondition: string;
      Price: {
        LandedPrice: {
          CurrencyCode: string;
          Amount: number;
        };
        ListingPrice: {
          CurrencyCode: string;
          Amount: number;
        };
        Shipping: {
          CurrencyCode: string;
          Amount: number;
        };
      };
      CompetitivePriceId: string;
    }>;
    NumberOfOfferListings: Array<{
      condition: string;
      Count: number;
    }>;
  };
  RankData?: any;
}

interface AmazonRank {
  Marketplace: string;
  Rank: number;
  Title?: string;
  Link?: string;
}

/**
 * Obtém a URL da webhook da Amazon a partir do Firestore
 * @returns URL da webhook ou null se não encontrada
 */
async function getAmazonWebhookUrl(): Promise<string | null> {
  try {
    const docRef = doc(db, 'config', 'multicanal');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().amazon?.webhookUrl) {
      return docSnap.data().amazon.webhookUrl;
    }
    
    console.error('URL da webhook da Amazon não encontrada no Firestore');
    return null;
  } catch (error) {
    console.error('Erro ao buscar URL da webhook:', error);
    return null;
  }
}

/**
 * Obtém o preço de um produto na Amazon pelo EAN usando a webhook configurada
 * @param ean Código EAN do produto
 * @returns O preço do produto ou null se não encontrado
 */
export async function getProductPriceByEAN(ean: string): Promise<number | null> {
  try {
    // Buscar a URL da webhook no Firestore
    const webhookUrl = await getAmazonWebhookUrl();
    
    if (!webhookUrl) {
      console.error('Não foi possível obter a URL da webhook da Amazon');
      return null;
    }
    
    console.log(`Enviando EAN ${ean} para webhook: ${webhookUrl}`);
    
    // Enviar o EAN para a webhook
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ean })
    });
    
    if (!response.ok) {
      throw new Error(`Webhook error: ${response.statusText}`);
    }
    
    // A resposta da webhook é um array de objetos AmazonProductData
    const productDataArray = await response.json() as AmazonProductData[];
    console.log('Resposta da webhook recebida');
    
    if (!Array.isArray(productDataArray) || productDataArray.length === 0) {
      console.error('Formato de resposta inesperado');
      return null;
    }
    
    // Obter o primeiro item do array
    const productData = productDataArray[0];
    console.log('Dados do produto processados:', productData ? 'Sucesso' : 'Falha');
    
    if (!productData || !productData.ASIN) {
      console.error('Dados do produto não encontrados na resposta');
      return null;
    }
    
    // Obter o preço da buybox
    if (productData.Summary && productData.Summary.BuyBoxPrices && productData.Summary.BuyBoxPrices.length > 0) {
      const buyBoxPrice = productData.Summary.BuyBoxPrices[0].LandedPrice.Amount;
      console.log(`Preço da buybox: ${buyBoxPrice} ${productData.Summary.BuyBoxPrices[0].LandedPrice.CurrencyCode}`);
      
      // Garantir que o preço tenha no máximo 2 casas decimais
      return parseFloat(parseFloat(buyBoxPrice.toString()).toFixed(2));
    }
    
    // Tentar obter o preço da primeira oferta se não houver buybox
    if (productData.Offers && productData.Offers.length > 0) {
      const firstOfferPrice = productData.Offers[0].ListingPrice.Amount;
      console.log(`Preço da primeira oferta: ${firstOfferPrice} ${productData.Offers[0].ListingPrice.CurrencyCode}`);
      
      // Garantir que o preço tenha no máximo 2 casas decimais
      return parseFloat(parseFloat(firstOfferPrice.toString()).toFixed(2));
    }
    
    console.error('Preço da buybox e ofertas não encontrados');
    return null;
  } catch (error) {
    console.error(`Error fetching price for product with EAN ${ean}:`, error);
    return null;
  }
}

/**
 * Obtém informações detalhadas de um produto na Amazon pelo EAN
 * @param ean Código EAN do produto
 * @returns Informações detalhadas do produto ou null se não encontrado
 */
export async function getProductDetailsByEAN(ean: string): Promise<AmazonProductData | null> {
  try {
    // Obter a URL da webhook do Firestore
    const webhookUrl = await getAmazonWebhookUrl();
    if (!webhookUrl) {
      console.error('URL da webhook da Amazon não configurada');
      return null;
    }

    // Enviar requisição para a webhook
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ean }),
    });

    if (!response.ok) {
      console.error(`Erro ao obter dados do produto com EAN ${ean}: ${response.statusText}`);
      return null;
    }

    const responseData = await response.json();
    
    // Log detalhado da resposta completa para debug
    console.log('Resposta completa da webhook da Amazon:', JSON.stringify(responseData, null, 2));
    
    // Novo formato: a resposta é um array com um objeto que tem um campo 'body' contendo uma string JSON
    if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].body) {
      try {
        // Parsear a string JSON do campo 'body'
        const bodyData = JSON.parse(responseData[0].body);
        console.log('Dados parseados do body:', JSON.stringify(bodyData, null, 2));
        
        // Verificar se o resultado é um array e pegar o primeiro item
        const productData = Array.isArray(bodyData) ? bodyData[0] : bodyData;
        
        if (!productData || !productData.ASIN) {
          console.error('Dados do produto não encontrados na resposta parseada');
          return null;
        }
        
        console.log('Processando dados do produto com ASIN:', productData.ASIN);
        
        // Log específico para verificar os dados de ranking
        console.log('Verificando dados de ranking...');
        if (productData.salesRanks) {
          console.log('Dados de Ranking encontrados em salesRanks:', JSON.stringify(productData.salesRanks, null, 2));
        } else if (productData.Summary && productData.Summary.SalesRankings) {
          console.log('Dados de Ranking encontrados em Summary.SalesRankings:', JSON.stringify(productData.Summary.SalesRankings, null, 2));
        } else {
          console.log('Nenhum dado de Ranking encontrado nas estruturas conhecidas');
          console.log('Propriedades disponíveis na resposta:', Object.keys(productData));
        }
        
        return productData as AmazonProductData;
      } catch (parseError) {
        console.error('Erro ao parsear o campo body da resposta:', parseError);
        return null;
      }
    } else {
      // Formato antigo: tentar processar diretamente
      const data = Array.isArray(responseData) ? responseData[0] : responseData;
      
      if (!data || !data.ASIN) {
        console.error('Dados do produto não encontrados na resposta');
        return null;
      }
      
      console.log('Processando dados do produto com ASIN:', data.ASIN);
      
      // Log específico para verificar os dados de ranking
      console.log('Verificando dados de ranking...');
      if (data.salesRanks) {
        console.log('Dados de Ranking encontrados em salesRanks:', JSON.stringify(data.salesRanks, null, 2));
      } else if (data.Summary && data.Summary.SalesRankings) {
        console.log('Dados de Ranking encontrados em Summary.SalesRankings:', JSON.stringify(data.Summary.SalesRankings, null, 2));
      } else {
        console.log('Nenhum dado de Ranking encontrado nas estruturas conhecidas');
        console.log('Propriedades disponíveis na resposta:', Object.keys(data));
      }

      return data as AmazonProductData;
    }
  } catch (error) {
    console.error(`Erro ao obter dados do produto com EAN ${ean}:`, error);
    return null;
  }
}

/**
 * Busca ofertas existentes no banco
 * @param ean Código EAN do produto
 * @returns Ofertas existentes
 */
async function getExistingOffers(ean: string): Promise<{
  primarySupplier: { proposedPrice: number } | null;
  reserveSupplier: { proposedPrice: number } | null;
}> {
  try {
    const q = query(
      collection(db, 'marketplaceProductSubmissions'),
      where('ean', '==', ean),
      where('desiredChannel', '==', 'amazon'),
      where('status', 'in', ['approved_primary', 'approved_reserve'])
    );
    
    const querySnapshot = await getDocs(q);
    let primarySupplier = null;
    let reserveSupplier = null;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'approved_primary') {
        primarySupplier = { proposedPrice: data.proposedPrice };
      } else if (data.status === 'approved_reserve') {
        reserveSupplier = { proposedPrice: data.proposedPrice };
      }
    });

    console.log('Ofertas existentes encontradas:', { primarySupplier, reserveSupplier });
    
    return { primarySupplier, reserveSupplier };
  } catch (error) {
    console.error('Erro ao buscar ofertas existentes:', error);
    return { primarySupplier: null, reserveSupplier: null };
  }
}

/**
 * Obtém preços de um produto na Amazon
 * @param ean Código EAN do produto
 * @returns Preços do produto
 */
async function getAmazonPrices(ean: string): Promise<{
  buyboxPrice: number | null;
  ptPrice: number | null;
  esPrice: number | null;
}> {
  try {
    const productData = await getProductDetailsByEAN(ean);
    
    if (!productData) {
      return {
        buyboxPrice: null,
        ptPrice: null,
        esPrice: null
      };
    }
    
    // Obter o preço da buybox
    let buyboxPrice: number | null = null;
    if (productData.Summary && productData.Summary.BuyBoxPrices && productData.Summary.BuyBoxPrices.length > 0) {
      buyboxPrice = productData.Summary.BuyBoxPrices[0].LandedPrice.Amount;
      console.log(`Preço da buybox: ${buyboxPrice}`);
    } else if (productData.Offers && productData.Offers.length > 0) {
      // Usar o preço da primeira oferta se não houver buybox
      const buyBoxWinner = productData.Offers.find(offer => offer.IsBuyBoxWinner);
      if (buyBoxWinner) {
        buyboxPrice = buyBoxWinner.ListingPrice.Amount;
        console.log(`Preço do vencedor da buybox: ${buyboxPrice}`);
      } else {
        buyboxPrice = productData.Offers[0].ListingPrice.Amount;
        console.log(`Preço da primeira oferta: ${buyboxPrice}`);
      }
    }
    
    // Obter preços por países
    const pricesByCountry = getPricesByCountry(productData.Offers || []);
    console.log('Preços por países:', pricesByCountry);
    
    return {
      buyboxPrice,
      ptPrice: pricesByCountry.ptPrice,
      esPrice: pricesByCountry.esPrice
    };
  } catch (error) {
    console.error('Erro ao obter preços da Amazon:', error);
    return {
      buyboxPrice: null,
      ptPrice: null,
      esPrice: null
    };
  }
}

/**
 * Calcula o preço sugerido com base nos preços existentes
 * @param ean Código EAN do produto
 * @param proposedPrice Preço proposto
 * @returns Preço sugerido e tipo de fornecedor
 */
export async function calculatePriceRecommendation(ean: string, proposedPrice: number) {
  try {
    // Buscar ofertas existentes primeiro
    const existingOffers = await getExistingOffers(ean);
    console.log('Ofertas existentes:', existingOffers);

    // Buscar preços da Amazon
    const amazonPrices = await getAmazonPrices(ean);
    
    // Se existe fornecedor principal, usar seu preço como base
    if (existingOffers.primarySupplier) {
      const basePrice = existingOffers.primarySupplier.proposedPrice;
      console.log('Usando preço do fornecedor principal:', basePrice);
      
      // Calcular comissões
      const commission = Math.round(basePrice * 0.23 * 100) / 100;
      const commissionVAT = Math.round(commission * 0.23 * 100) / 100;
      const commissionWithVAT = commission + commissionVAT;
      const valueToReceive = basePrice - commissionWithVAT;

      return {
        basePrice,
        recommendedPrice: basePrice,
        commission,
        commissionVAT,
        commissionWithVAT,
        valueToReceive,
        supplierType: proposedPrice < basePrice ? 
          'primary' : 
          'reserve',
        buyboxPrice: amazonPrices.buyboxPrice,
        ptPrice: amazonPrices.ptPrice,
        esPrice: amazonPrices.esPrice,
        reason: 'Preço baseado no fornecedor principal existente'
      };
    }
    
    // Se existe fornecedor reserva, usar seu preço como base
    if (existingOffers.reserveSupplier) {
      const basePrice = existingOffers.reserveSupplier.proposedPrice;
      console.log('Usando preço do fornecedor reserva:', basePrice);
      
      // Calcular comissões
      const commission = Math.round(basePrice * 0.23 * 100) / 100;
      const commissionVAT = Math.round(commission * 0.23 * 100) / 100;
      const commissionWithVAT = commission + commissionVAT;
      const valueToReceive = basePrice - commissionWithVAT;

      return {
        basePrice,
        recommendedPrice: basePrice,
        commission,
        commissionVAT,
        commissionWithVAT,
        valueToReceive,
        supplierType: 'primary',
        buyboxPrice: amazonPrices.buyboxPrice,
        ptPrice: amazonPrices.ptPrice,
        esPrice: amazonPrices.esPrice,
        reason: 'Preço baseado no fornecedor reserva existente'
      };
    }

    // Se não existe nenhum fornecedor, usar preço de Portugal ou buybox como base
    console.log('Sem ofertas existentes, usando preço base de', amazonPrices.ptPrice || amazonPrices.buyboxPrice, '(PT ou buybox)');
    
    // Calcular preço recomendado baseado nos preços existentes
    let recommendedPrice = null;
    let reason = '';
  
    if (amazonPrices.ptPrice !== null) {
      // Se existe preço em Portugal, recomendamos 0.50€ abaixo
      recommendedPrice = Number((amazonPrices.ptPrice - 0.50).toFixed(2));
      reason = 'Preço 0.50€ abaixo do valor em Portugal para garantir competitividade';
    } else if (amazonPrices.buyboxPrice !== null) {
      // Se não tem preço em Portugal mas tem buybox, usamos buybox - 0.50€
      recommendedPrice = Number((amazonPrices.buyboxPrice - 0.50).toFixed(2));
      reason = 'Preço 0.50€ abaixo da buybox para garantir competitividade';
    } else {
      // Se não tem nenhum preço de referência, usamos o preço base
      recommendedPrice = amazonPrices.ptPrice || amazonPrices.buyboxPrice;
      reason = 'Preço baseado no valor base por falta de referências';
    }

    // Calcular comissões sobre o preço recomendado
    const commission = recommendedPrice * 0.20; // 20% de comissão
    const commissionVAT = commission * 0.23; // 23% IVA sobre a comissão
    const valueToReceive = recommendedPrice - commission - commissionVAT;

    console.log('Recomendação de preço:', {
      basePrice: amazonPrices.ptPrice || amazonPrices.buyboxPrice,
      recommendedPrice,
      reason,
      ptPrice: amazonPrices.ptPrice,
      buyboxPrice: amazonPrices.buyboxPrice
    });

    return {
      basePrice: amazonPrices.ptPrice || amazonPrices.buyboxPrice,
      recommendedPrice,
      reason,
      commission: Number(commission.toFixed(2)),
      commissionVAT: Number(commissionVAT.toFixed(2)),
      commissionWithVAT: Number((commission + commissionVAT).toFixed(2)),
      valueToReceive: Number(valueToReceive.toFixed(2)),
      supplierType: 'primary',
      buyboxPrice: amazonPrices.buyboxPrice,
      ptPrice: amazonPrices.ptPrice,
      esPrice: amazonPrices.esPrice,
      totalOffers: amazonPrices.totalOffers
    };
  } catch (error) {
    console.error('Erro ao calcular recomendação de preço:', error);
    return {
      basePrice: null,
      recommendedPrice: null,
      commission: null,
      commissionVAT: null,
      commissionWithVAT: null,
      valueToReceive: null,
      supplierType: null,
      buyboxPrice: null,
      ptPrice: null,
      esPrice: null,
      reason: 'Erro ao calcular recomendação de preço'
    };
  }
}

/**
 * Verifica se um produto é elegível para venda na Amazon com base em suas ofertas
 * @param ean Código EAN do produto
 * @param proposedPrice Preço proposto para o produto
 * @returns Objeto com informações de elegibilidade
 */
export async function checkAmazonEligibility(ean: string, proposedPrice: number): Promise<{
  eligible: boolean;
  buyboxPrice: number | null;
  competitivePrice: number | null;
  totalOffers: number | null;
  ptPrice: number | null;
  esPrice: number | null;
  priceRange: string | null;
  recommendedPrice: number | null;
  valueToReceive: number | null;
  commission: number | null;
  commissionWithVAT: number | null;
  supplierType: 'primary' | 'reserve' | 'not_eligible' | null;
  rankValue: number | null;
  rankCategory: 'excellent' | 'good' | 'average' | 'moderate' | null;
  rankMessage: string;
  reason?: string;
}> {
  try {
    const productData = await getProductDetailsByEAN(ean);
    
    if (!productData) {
      return {
        eligible: false,
        buyboxPrice: null,
        competitivePrice: null,
        totalOffers: null,
        ptPrice: null,
        esPrice: null,
        priceRange: null,
        recommendedPrice: null,
        valueToReceive: null,
        commission: null,
        commissionWithVAT: null,
        supplierType: null,
        rankValue: null,
        rankCategory: null,
        rankMessage: 'Sem informações de ranking disponíveis',
        reason: 'Produto não encontrado na Amazon'
      };
    }
    
    console.log('Verificando elegibilidade para produto:', productData.ASIN);
    
    // Obter o preço da buybox
    let buyboxPrice: number | null = null;
    
    // Verificar diferentes locais onde o preço da buybox pode estar
    if (productData.Summary && productData.Summary.BuyBoxPrices && productData.Summary.BuyBoxPrices.length > 0) {
      // Formato original
      buyboxPrice = productData.Summary.BuyBoxPrices[0].LandedPrice.Amount;
      console.log(`Preço da buybox (BuyBoxPrices): ${buyboxPrice}`);
    } else if (productData.Summary && productData.Summary.LowestPrices && productData.Summary.LowestPrices.length > 0) {
      // Novo formato - usar o preço mais baixo da Amazon como buybox
      const amazonPrice = productData.Summary.LowestPrices.find(p => p.fulfillmentChannel === 'Amazon');
      if (amazonPrice) {
        buyboxPrice = amazonPrice.LandedPrice.Amount;
        console.log(`Preço da buybox (LowestPrices Amazon): ${buyboxPrice}`);
      } else {
        // Se não houver preço da Amazon, usar o preço mais baixo disponível
        buyboxPrice = productData.Summary.LowestPrices[0].LandedPrice.Amount;
        console.log(`Preço da buybox (LowestPrices primeiro): ${buyboxPrice}`);
      }
    } else if (productData.Offers && productData.Offers.length > 0) {
      // Usar o preço da primeira oferta se não houver buybox
      const buyBoxWinner = productData.Offers.find(offer => offer.IsBuyBoxWinner);
      if (buyBoxWinner) {
        buyboxPrice = buyBoxWinner.ListingPrice.Amount + (buyBoxWinner.Shipping?.Amount || 0);
        console.log(`Preço do vencedor da buybox (Offers): ${buyboxPrice}`);
      } else {
        // Ordenar ofertas por preço e pegar a mais barata
        const sortedOffers = [...productData.Offers].sort((a, b) => {
          const priceA = a.ListingPrice.Amount + (a.Shipping?.Amount || 0);
          const priceB = b.ListingPrice.Amount + (b.Shipping?.Amount || 0);
          return priceA - priceB;
        });
        
        buyboxPrice = sortedOffers[0].ListingPrice.Amount + (sortedOffers[0].Shipping?.Amount || 0);
        console.log(`Preço da oferta mais barata: ${buyboxPrice}`);
      }
    }
    
    // Obter o preço competitivo
    let competitivePrice: number | null = null;
    if (productData.Summary && productData.Summary.CompetitivePriceThreshold) {
      competitivePrice = productData.Summary.CompetitivePriceThreshold.Amount;
      console.log(`Preço competitivo (CompetitivePriceThreshold): ${competitivePrice}`);
    } else if (productData.Summary && productData.Summary.SuggestedLowerPricePlusShipping) {
      // Novo formato - usar SuggestedLowerPricePlusShipping
      competitivePrice = productData.Summary.SuggestedLowerPricePlusShipping.Amount;
      console.log(`Preço competitivo (SuggestedLowerPricePlusShipping): ${competitivePrice}`);
    }
    
    // Obter o total de ofertas
    let totalOffers: number | null = null;
    if (productData.Summary && productData.Summary.TotalOfferCount !== undefined) {
      // Formato original
      totalOffers = productData.Summary.TotalOfferCount;
      console.log(`Total de ofertas (TotalOfferCount): ${totalOffers}`);
    } else if (productData.Summary && productData.Summary.NumberOfOffers) {
      // Novo formato - somar todas as ofertas em NumberOfOffers
      totalOffers = productData.Summary.NumberOfOffers.reduce((total, item) => {
        return total + item.OfferCount;
      }, 0);
      console.log(`Total de ofertas (NumberOfOffers somado): ${totalOffers}`);
    } else if (productData.Offers) {
      // Fallback para o tamanho do array de ofertas
      totalOffers = productData.Offers.length;
      console.log(`Total de ofertas (tamanho do array Offers): ${totalOffers}`);
    } else {
      totalOffers = 0;
      console.log('Nenhuma oferta encontrada');
    }
    
    // Obter preços por países
    const pricesByCountry = getPricesByCountry(productData.Offers || []);
    console.log('Preços por países:', pricesByCountry);
    
    // Analisar o ranking do produto
    const rankingAnalysis = analyzeProductRanking(productData);
    console.log('Análise de ranking:', rankingAnalysis);
    
    // Calcular o preço sugerido
    const priceRecommendation = await calculatePriceRecommendation(ean, proposedPrice);
    console.log('Recomendação de preço:', priceRecommendation);

    // Verificar elegibilidade
    let eligible = false;
    let reason = '';

    if (!buyboxPrice && !pricesByCountry.ptPrice) {
      reason = 'Não foi possível obter o preço de referência';
    } else if (totalOffers && totalOffers > 10) {
      reason = `Muitas ofertas concorrentes (${totalOffers})`;
    } else if (priceRecommendation.recommendedPrice && proposedPrice > priceRecommendation.recommendedPrice) {
      reason = `O preço proposto (${proposedPrice.toFixed(2)}€) está acima do preço recomendado (${priceRecommendation.recommendedPrice.toFixed(2)}€)`;
    } else {
      eligible = true;
      
      // Adicionar informação sobre o ranking se disponível
      if (rankingAnalysis.rankCategory === 'excellent') {
        reason = rankingAnalysis.rankMessage;
      } else if (rankingAnalysis.rankCategory === 'good') {
        reason = 'Produto com bom potencial de vendas';
      } else if (rankingAnalysis.rankCategory === 'fair') {
        reason = 'Produto com potencial de vendas moderado';
      } else {
        reason = 'Produto elegível para venda';
      }
      
      // Adicionar informação sobre o tipo de fornecedor
      if (priceRecommendation.supplierType) {
        reason += priceRecommendation.supplierType === 'primary' ? 
          ' - Pode ser fornecedor principal' : 
          ' - Pode ser fornecedor reserva';
      }
    }

    return {
      eligible,
      buyboxPrice,
      competitivePrice: pricesByCountry.highestPrice,
      totalOffers,
      ptPrice: pricesByCountry.ptPrice,
      esPrice: pricesByCountry.esPrice,
      priceRange: pricesByCountry.priceRange,
      recommendedPrice: priceRecommendation.recommendedPrice,
      valueToReceive: priceRecommendation.valueToReceive,
      commission: priceRecommendation.commission,
      commissionWithVAT: priceRecommendation.commissionWithVAT,
      supplierType: eligible ? priceRecommendation.supplierType : 'not_eligible',
      rankValue: rankingAnalysis.rankValue,
      rankCategory: rankingAnalysis.rankCategory,
      rankMessage: rankingAnalysis.rankMessage,
      reason
    };
  } catch (error) {
    console.error(`Error checking eligibility for product with EAN ${ean}:`, error);
    return {
      eligible: false,
      buyboxPrice: null,
      competitivePrice: null,
      totalOffers: null,
      ptPrice: null,
      esPrice: null,
      priceRange: null,
      recommendedPrice: null,
      valueToReceive: null,
      commission: null,
      commissionWithVAT: null,
      supplierType: null,
      rankValue: null,
      rankCategory: null,
      rankMessage: 'Erro ao obter informações de ranking',
      reason: 'Erro ao verificar elegibilidade'
    };
  }
}

/**
 * Obtém preços de um produto em diferentes marketplaces da Amazon
 * @param ean Código EAN do produto
 * @returns Objeto com os preços nos diferentes marketplaces
 */
export async function getProductPricesAcrossMarketplaces(ean: string): Promise<Record<string, number | null>> {
  // Por enquanto, apenas retornamos o preço principal
  // Quando a webhook suportar múltiplos marketplaces, podemos expandir esta função
  const price = await getProductPriceByEAN(ean);
  
  return {
    'amazon.es': price,
    'amazon.com': null,
    'amazon.co.uk': null,
    'amazon.de': null,
    'amazon.fr': null,
    'amazon.it': null
  };
}

/**
 * Analisa o ranking de vendas do produto e retorna uma avaliação
 * @param productData Dados completos do produto
 * @returns Avaliação do ranking e mensagem
 */
function analyzeProductRanking(productData: any): {
  rankValue: number | null;
  rankCategory: 'excellent' | 'good' | 'average' | 'moderate' | null;
  rankMessage: string;
} {
  if (!productData) {
    return {
      rankValue: null,
      rankCategory: null,
      rankMessage: 'Sem informações de ranking disponíveis'
    };
  }
  
  console.log('Analisando ranking para produto:', JSON.stringify(productData.ASIN || 'desconhecido'));
  
  // Verificar diferentes locais onde os dados de ranking podem estar
  let bestRank = null;
  let rankTitle = '';
  
  // Verificar em salesRanks (formato observado na resposta)
  if (productData.salesRanks && productData.salesRanks.length > 0) {
    for (const salesRank of productData.salesRanks) {
      // Verificar em classificationRanks
      if (salesRank.classificationRanks && salesRank.classificationRanks.length > 0) {
        for (const classRank of salesRank.classificationRanks) {
          if (bestRank === null || classRank.rank < bestRank) {
            bestRank = classRank.rank;
            rankTitle = classRank.title || 'categoria específica';
            console.log(`Encontrado ranking em classificationRanks: #${bestRank} em ${rankTitle}`);
          }
        }
      }
      
      // Verificar em displayGroupRanks
      if (salesRank.displayGroupRanks && salesRank.displayGroupRanks.length > 0) {
        for (const displayRank of salesRank.displayGroupRanks) {
          if (bestRank === null || displayRank.rank < bestRank) {
            bestRank = displayRank.rank;
            rankTitle = displayRank.title || 'categoria geral';
            console.log(`Encontrado ranking em displayGroupRanks: #${bestRank} em ${rankTitle}`);
          }
        }
      }
    }
  }
  
  // Verificar em Summary.SalesRankings (formato alternativo)
  if (productData.Summary && productData.Summary.SalesRankings && productData.Summary.SalesRankings.length > 0) {
    for (const ranking of productData.Summary.SalesRankings) {
      if (bestRank === null || ranking.Rank < bestRank) {
        bestRank = ranking.Rank;
        rankTitle = ranking.ProductCategoryId || 'categoria';
        console.log(`Encontrado ranking em Summary.SalesRankings: #${bestRank} em ${rankTitle}`);
      }
    }
  }
  
  // NOVO FORMATO: Verificar em body.Summary.SalesRankings
  if (!bestRank && productData.body) {
    try {
      // Se body for uma string, tentar parsear
      const bodyData = typeof productData.body === 'string' ? JSON.parse(productData.body) : productData.body;
      
      if (Array.isArray(bodyData) && bodyData.length > 0) {
        const firstItem = bodyData[0];
        
        if (firstItem.Summary && firstItem.Summary.SalesRankings && firstItem.Summary.SalesRankings.length > 0) {
          for (const ranking of firstItem.Summary.SalesRankings) {
            if (bestRank === null || ranking.Rank < bestRank) {
              bestRank = ranking.Rank;
              rankTitle = ranking.ProductCategoryId || 'categoria';
              console.log(`Encontrado ranking em body.Summary.SalesRankings: #${bestRank} em ${rankTitle}`);
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao processar campo body para ranking:', error);
    }
  }
  
  // Se ainda não temos ranking, retornar null
  if (bestRank === null) {
    console.log('Nenhum ranking encontrado após verificar todas as estruturas');
    return {
      rankValue: null,
      rankCategory: null,
      rankMessage: 'Sem informações de ranking disponíveis'
    };
  }
  
  console.log(`Melhor ranking encontrado: #${bestRank} em ${rankTitle}`);
  
  // Categorizar o ranking e gerar mensagem
  let rankCategory: 'excellent' | 'good' | 'average' | 'moderate' = 'moderate';
  let rankMessage = '';
  
  if (bestRank <= 100) {
    rankCategory = 'excellent';
    rankMessage = `Produto no TOP 100! Posição ${bestRank} em ${rankTitle}. Excelente potencial de vendas!`;
  } else if (bestRank <= 500) {
    rankCategory = 'good';
    rankMessage = `Produto com bom potencial! Posição ${bestRank} em ${rankTitle}.`;
  } else if (bestRank <= 1000) {
    rankCategory = 'average';
    rankMessage = `Produto com potencial razoável. Posição ${bestRank} em ${rankTitle}.`;
  } else {
    rankCategory = 'moderate';
    rankMessage = `Posição ${bestRank} em ${rankTitle}. Considere produtos melhor posicionados.`;
  }
  
  return {
    rankValue: bestRank,
    rankCategory,
    rankMessage
  };
}

/**
 * Obtém informações de preços por países a partir das ofertas da Amazon
 * @param offers Lista de ofertas da Amazon
 * @returns Objeto com preços por países e informações adicionais
 */
function getPricesByCountry(offers: AmazonOffer[]): {
  lowestPrice: number | null;
  lowestPriceCountry: string | null;
  ptPrice: number | null;
  ptSellerId: string | null;
  esPrice: number | null;
  esSellerId: string | null;
  highestPrice: number | null;
  priceRange: string | null;
} {
  if (!offers || offers.length === 0) {
    console.log('Nenhuma oferta encontrada para análise de preços por país');
    return {
      lowestPrice: null,
      lowestPriceCountry: null,
      ptPrice: null,
      ptSellerId: null,
      esPrice: null,
      esSellerId: null,
      highestPrice: null,
      priceRange: null
    };
  }

  console.log(`Analisando ${offers.length} ofertas para obter preços por país`);

  // Inicializar valores
  let lowestPrice = Number.MAX_VALUE;
  let lowestPriceCountry = null;
  let ptPrice = null;
  let ptSellerId = null;
  let esPrice = null;
  let esSellerId = null;
  let highestPrice = 0;

  // Processar cada oferta
  offers.forEach(offer => {
    // Verificar se a oferta tem a estrutura esperada
    if (!offer.ListingPrice || typeof offer.ListingPrice.Amount !== 'number') {
      console.log('Oferta com estrutura inválida:', offer);
      return; // Pular esta oferta
    }

    const price = offer.ListingPrice.Amount + (offer.Shipping?.Amount || 0);
    const country = offer.ShipsFrom?.Country || 'Unknown';

    console.log(`Oferta de ${country}: ${price}€ (Vendedor: ${offer.SellerId})`);

    // Atualizar preço mais baixo
    if (price < lowestPrice) {
      lowestPrice = price;
      lowestPriceCountry = country;
    }

    // Atualizar preço mais alto
    if (price > highestPrice) {
      highestPrice = price;
    }

    // Registrar preço de Portugal
    if (country === 'PT' && (ptPrice === null || price < ptPrice)) {
      ptPrice = Number(price);
      ptSellerId = offer.SellerId;
      console.log(`Preço de Portugal atualizado: ${ptPrice}€`);
    }

    // Registrar preço de Espanha
    if (country === 'ES' && (esPrice === null || price < esPrice)) {
      esPrice = Number(price);
      esSellerId = offer.SellerId;
      console.log(`Preço de Espanha atualizado: ${esPrice}€`);
    }
  });

  // Se não encontramos preços específicos por país, usar o preço mais baixo como referência
  if (ptPrice === null && lowestPrice !== Number.MAX_VALUE) {
    console.log(`Nenhum preço de Portugal encontrado, usando o mais baixo: ${lowestPrice}€`);
    ptPrice = lowestPrice;
  }

  if (esPrice === null && lowestPrice !== Number.MAX_VALUE) {
    console.log(`Nenhum preço de Espanha encontrado, usando o mais baixo: ${lowestPrice}€`);
    esPrice = lowestPrice;
  }

  // Formatar o intervalo de preços
  const priceRange = lowestPrice !== Number.MAX_VALUE && highestPrice > 0
    ? `${lowestPrice.toFixed(2)}€ - ${highestPrice.toFixed(2)}€`
    : null;

  // Ajustar o preço mais baixo se não foi encontrado
  if (lowestPrice === Number.MAX_VALUE) {
    lowestPrice = null;
  }

  console.log('Resultado da análise de preços por país:', {
    lowestPrice,
    lowestPriceCountry,
    ptPrice,
    esPrice,
    priceRange
  });

  return {
    lowestPrice,
    lowestPriceCountry,
    ptPrice,
    ptSellerId,
    esPrice,
    esSellerId,
    highestPrice,
    priceRange
  };
}

/**
 * Calcula o preço com uma margem mínima
 * @param basePrice Preço base
 * @returns Preço com margem mínima
 */
function calculatePriceWithMinimumMargin(basePrice: number): number {
  // Definir margem mínima (1%)
  const minimumMargin = 0.01;
  
  // Calcular o preço com margem mínima
  const priceWithMargin = basePrice * (1 + minimumMargin);
  
  // Arredondar para 2 casas decimais
  return Math.round(priceWithMargin * 100) / 100;
}
